import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { ToolConfig } from "@/api/admin-api"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"

const DEFAULT_MIN = 3
const DEFAULT_MAX = 20
const SAFE_SEARCH_VALUES = ["off", "moderate", "strict"] as const
type SafeSearchValue = (typeof SAFE_SEARCH_VALUES)[number]

const COUNTRY_OPTIONS: { code: string; label: string }[] = [
  { code: "SE", label: "Sweden (SE)" },
  { code: "NO", label: "Norway (NO)" },
  { code: "DK", label: "Denmark (DK)" },
  { code: "FI", label: "Finland (FI)" },
  { code: "DE", label: "Germany (DE)" },
  { code: "FR", label: "France (FR)" },
  { code: "GB", label: "United Kingdom (GB)" },
  { code: "US", label: "United States (US)" },
  { code: "ES", label: "Spain (ES)" },
  { code: "IT", label: "Italy (IT)" },
  { code: "NL", label: "Netherlands (NL)" },
]

type WebSearchFormState = {
  defaultNumResults: string
  maxNumResults: string
  country: string
  safeSearch: SafeSearchValue | "default"
}

function toFormState(config: ToolConfig): WebSearchFormState {
  const rawDefault = config.default_num_results
  const rawMax = config.max_num_results
  const rawCountry = config.country
  const rawSafe = config.safe_search
  const safe: SafeSearchValue | "default" =
    typeof rawSafe === "string" && (SAFE_SEARCH_VALUES as readonly string[]).includes(rawSafe)
      ? (rawSafe as SafeSearchValue)
      : "default"
  return {
    defaultNumResults: typeof rawDefault === "number" ? String(rawDefault) : "",
    maxNumResults: typeof rawMax === "number" ? String(rawMax) : "",
    country: typeof rawCountry === "string" ? rawCountry.toUpperCase() : "",
    safeSearch: safe,
  }
}

function parseNumericField(value: string): number | null {
  if (!value.trim()) return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  const rounded = Math.round(n)
  return Math.max(DEFAULT_MIN, Math.min(rounded, DEFAULT_MAX))
}

export type ToolConfigDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  toolName: string
  config: ToolConfig | null
  isLoading: boolean
  isSaving: boolean
  onSave: (next: ToolConfig) => Promise<void> | void
}

export function ToolConfigDialog({
  open,
  onOpenChange,
  toolName,
  config,
  isLoading,
  isSaving,
  onSave,
}: ToolConfigDialogProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState<WebSearchFormState>(() => toFormState(config ?? {}))

  useEffect(() => {
    if (open) {
      setForm(toFormState(config ?? {}))
    }
  }, [open, config])

  const validationError = useMemo(() => {
    const d = parseNumericField(form.defaultNumResults)
    const m = parseNumericField(form.maxNumResults)
    if (d !== null && m !== null && d > m) {
      return t("adminDashboard.tools.config.errorDefaultExceedsMax", {
        defaultValue: "Default results cannot exceed max results.",
      })
    }
    return null
  }, [form.defaultNumResults, form.maxNumResults, t])

  const handleSave = async () => {
    if (validationError) return
    const patch: ToolConfig = {
      default_num_results: parseNumericField(form.defaultNumResults),
      max_num_results: parseNumericField(form.maxNumResults),
      country: form.country.trim() ? form.country.trim().toUpperCase() : null,
      safe_search: form.safeSearch === "default" ? null : form.safeSearch,
    }
    await onSave(patch)
  }

  const isWebSearch = toolName === "web_search"

  const renderDialogBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-6">
          <Spinner />
        </div>
      )
    }

    if (!isWebSearch) {
      return (
        <p className="text-muted-foreground py-6 text-sm">
          {t("adminDashboard.tools.config.unsupported", {
            defaultValue: "This tool has no admin-configurable settings.",
          })}
        </p>
      )
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="web-search-default">
              {t("adminDashboard.tools.config.defaultNumResults", {
                defaultValue: "Default results",
              })}
            </Label>
            <Input
              id="web-search-default"
              inputMode="numeric"
              max={DEFAULT_MAX}
              min={DEFAULT_MIN}
              placeholder="10"
              type="number"
              value={form.defaultNumResults}
              onChange={(e) => setForm((prev) => ({ ...prev, defaultNumResults: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="web-search-max">
              {t("adminDashboard.tools.config.maxNumResults", {
                defaultValue: "Max results",
              })}
            </Label>
            <Input
              id="web-search-max"
              inputMode="numeric"
              max={DEFAULT_MAX}
              min={DEFAULT_MIN}
              placeholder="20"
              type="number"
              value={form.maxNumResults}
              onChange={(e) => setForm((prev) => ({ ...prev, maxNumResults: e.target.value }))}
            />
          </div>
        </div>
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.tools.config.numResultsHint", {
            defaultValue: "Brave supports {{min}}–{{max}} results per query.",
            min: DEFAULT_MIN,
            max: DEFAULT_MAX,
          })}
        </p>

        <div className="space-y-2">
          <Label htmlFor="web-search-country">
            {t("adminDashboard.tools.config.country", { defaultValue: "Country" })}
          </Label>
          <Select
            value={form.country || "__default"}
            onValueChange={(value) =>
              setForm((prev) => ({ ...prev, country: value === "__default" ? "" : value }))
            }
          >
            <SelectTrigger id="web-search-country">
              <SelectValue
                placeholder={t("adminDashboard.tools.config.countryDefault", {
                  defaultValue: "Default (SE)",
                })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default">
                {t("adminDashboard.tools.config.countryDefault", {
                  defaultValue: "Default (SE)",
                })}
              </SelectItem>
              {COUNTRY_OPTIONS.map((opt) => (
                <SelectItem key={opt.code} value={opt.code}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="web-search-safe">
            {t("adminDashboard.tools.config.safeSearch", { defaultValue: "Safe search" })}
          </Label>
          <Select
            value={form.safeSearch}
            onValueChange={(value) =>
              setForm((prev) => ({
                ...prev,
                safeSearch: value as WebSearchFormState["safeSearch"],
              }))
            }
          >
            <SelectTrigger id="web-search-safe">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">
                {t("adminDashboard.tools.config.safeSearchDefault", {
                  defaultValue: "Default",
                })}
              </SelectItem>
              <SelectItem value="off">
                {t("adminDashboard.tools.config.safeSearchOff", { defaultValue: "Off" })}
              </SelectItem>
              <SelectItem value="moderate">
                {t("adminDashboard.tools.config.safeSearchModerate", {
                  defaultValue: "Moderate",
                })}
              </SelectItem>
              <SelectItem value="strict">
                {t("adminDashboard.tools.config.safeSearchStrict", {
                  defaultValue: "Strict",
                })}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {validationError && <p className="text-destructive text-xs">{validationError}</p>}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.tools.config.title", {
              defaultValue: "Configure {{tool}}",
              tool: toolName,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.tools.config.description", {
              defaultValue:
                "Adjust runtime defaults for this tool. Leave fields blank to use the built-in defaults.",
            })}
          </DialogDescription>
        </DialogHeader>

        {renderDialogBody()}

        <DialogFooter>
          <Button
            disabled={isSaving}
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            disabled={isSaving || isLoading || !isWebSearch || validationError !== null}
            type="button"
            onClick={handleSave}
          >
            {isSaving
              ? t("common.saving", { defaultValue: "Saving…" })
              : t("common.save", { defaultValue: "Save" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
